import { PrismicNextImage } from '@prismicio/next';
import { asText, Content, isFilled } from '@prismicio/client';

import {
  Box,
  Card,
  List,
  Wrapper,
  Heading,
  ListItem,
  CardBody,
  CardHero,
  CardFooter,
  Typography,
  CardContent,
  CardFeature,
} from '@preventx/oxygen';
import { RichText } from '@preventx/prismic-services';

// Types
import { ActionLink } from '@/types/components/actions';

// Services
import { retrieveIcon } from '@/services/utils/icons';
import { applicationEnv } from '@/services/env/shared';

// Components
import { Link } from '@/components/Actions/Link';
import { BrandLogo } from '@/components/Graphics/BrandLogo';
import { ActionLinks } from '@/components/Actions/ActionLinks';

export type WithServicesProps = {
  slice: Content.HeroSliceWithServices;
};

export const WithServices = ({ slice }: WithServicesProps) => {
  const { primary } = slice;
  const { lede, title, actions, services, figure_logo, figure_caption } =
    primary;

  const mappedActions = (actions ?? []).map((item): ActionLink => ({
    action: item.action,
    id: item.identifier,
    addOutline: item.add_ghost_effect,
    icon: item?.icon !== 'None' ? retrieveIcon(item.icon) : undefined,
  }));

  const { WebsiteUrl } = applicationEnv();

  return (
    <Wrapper size="lg">
      <Box component="div" addFlow className="hero__content">
        <Box component="div" addFlow>
          <Box component="div" addRepel>
            <Box component="div" addFlow>
              <Heading component="h1" addHeroTitle>
                {asText(title)}
              </Heading>
              <Typography component="p" className="lede">
                {asText(lede)}
              </Typography>
            </Box>

            {mappedActions.length > 0 && (
              <Box component="div" className="hero__actions">
                <ActionLinks idPrefix="hero-actions" actions={mappedActions} />
              </Box>
            )}

            {isFilled.select(figure_logo) && (
              <figure className="cluster flex-nowrap">
                <Box component="span" className="site-head__brand">
                  <BrandLogo logo={figure_logo} />
                </Box>

                {isFilled.richText(figure_caption) && (
                  <figcaption>{asText(figure_caption)}</figcaption>
                )}
              </figure>
            )}
          </Box>

          {(services ?? []).length > 0 && (
            <Box
              component="div"
              className="auto-grid"
              data-placement="auto-fit"
              style={{ ['--c6-grid-min-item-size' as string]: '20rem' }}
            >
              {(services ?? []).map((service, index) => {
                const { image, caption, title_link, footer_link } = service;

                return (
                  <CardFeature key={`hero-with-service-${index + 1}`}>
                    <Card component="div" variant="simple">
                      {isFilled.image(image) && (
                        <CardHero component="div">
                          <PrismicNextImage field={image} loading="eager" />
                        </CardHero>
                      )}

                      <CardBody>
                        <CardContent>
                          <Box component="div" addFlow>
                            {isFilled.link(title_link) && (
                              <Link
                                type="link"
                                as="default"
                                href={title_link}
                                retainSearchParams
                              >
                                <Heading component="h2" size="h3">
                                  {title_link.text ?? ''}
                                </Heading>
                              </Link>
                            )}

                            {!isFilled.link(title_link) && (
                              <Heading component="h2" size="h3">
                                {title_link.text ?? ''}
                              </Heading>
                            )}

                            <RichText
                              content={caption}
                              variant="default"
                              websiteUrl={WebsiteUrl}
                            />
                          </Box>
                        </CardContent>

                        {isFilled.link(footer_link) && (
                          <CardFooter>
                            <List component="ul" style="plain-list">
                              <ListItem>
                                <Link
                                  type="link"
                                  as="default"
                                  href={footer_link}
                                  retainSearchParams
                                >
                                  {footer_link.text}
                                </Link>
                              </ListItem>
                            </List>
                          </CardFooter>
                        )}
                      </CardBody>
                    </Card>
                  </CardFeature>
                );
              })}
            </Box>
          )}
        </Box>
      </Box>
    </Wrapper>
  );
};
